<dialog
  class="auth-backdrop"
  open
  aria-modal="true"
  aria-labelledby="public-auth-title"
  (click)="close()"
  (cancel)="close()"
>
  <section class="auth-shell" role="document" (click)="$event.stopPropagation()">
    <button type="button" class="close-btn" (click)="close()" aria-label="Close auth modal">
      <i class="bi bi-x-lg"></i>
    </button>

    <div class="auth-panel__body">
      <div class="auth-content">
        @if (!resetMode()) {
          <div class="auth-card-header">
            <h2 id="public-auth-title" class="auth-heading">Sign-in to your account</h2>
            <p class="auth-support">Log in to your account to access HYTEC website features.</p>
          </div>

          <form [formGroup]="loginForm" (ngSubmit)="login()" class="auth-form">
            <label class="form-control">
              <span class="form-label">Email Address</span>
              <input
                type="email"
                formControlName="email"
                class="input-field"
                placeholder="visitor@example.com"
                maxlength="60"
              />
            </label>

            <label class="form-control">
              <div class="password-label-row">
                <span class="form-label">Password</span>
                <button type="button" class="password-toggle" (click)="togglePasswordVisibility()">
                  {{ showPassword() ? 'Hide' : 'Show' }}
                </button>
              </div>
              <div class="password-field-wrap">
                <input
                  [type]="showPassword() ? 'text' : 'password'"
                  formControlName="password"
                  class="input-field"
                  placeholder="********"
                />
                <button
                  type="button"
                  class="password-eye-btn"
                  (click)="togglePasswordVisibility()"
                  aria-label="Toggle password visibility"
                >
                  <i class="bi" [ngClass]="showPassword() ? 'bi-eye' : 'bi-eye-slash'"></i>
                </button>
              </div>
            </label>

            <button type="button" class="link-button" (click)="openForgotPassword()">
              Forgot Password?
            </button>

            @if (loginError()) {
              <div class="alert-error">
                {{ loginError() }}
              </div>
            }

            <button
              type="submit"
              class="btn-primary w-full mt-2"
              [disabled]="loginForm.invalid || loginSending()"
              [attr.aria-busy]="loginSending()"
            >
              @if (loginSending()) {
                <span class="button-spinner" aria-hidden="true"></span>
                <span>Logging in...</span>
              } @else {
                <span>Log In</span>
              }
            </button>

            <p class="register-prompt">
              <em>Don't have an account?</em>
              <button type="button" class="register-link" (click)="openSignup()">Register</button>
            </p>

            @if (activeUser()) {
              <button type="button" class="btn-secondary w-full" (click)="logout()">
                Clear Account
              </button>
            }
          </form>
        } @else {
          <div class="auth-card-header">
            <h2 id="public-auth-title" class="auth-heading">
              {{ setPasswordMode() ? 'Set New Password' : 'Forgot Password' }}
            </h2>
            <p class="auth-support">
              {{
                setPasswordMode()
                  ? 'Create a new password for your HYTEC account.'
                  : 'Enter your email to receive an OTP or reset password link.'
              }}
            </p>
          </div>

          @if (setPasswordMode()) {
            <form [formGroup]="setPasswordForm" (ngSubmit)="completePasswordReset()" class="auth-form">
              <label class="form-control">
                <span class="form-label">Email Address</span>
                <input
                  type="email"
                  formControlName="email"
                  class="input-field"
                  readonly
                />
              </label>

              <label class="form-control">
                <span class="form-label">New Password</span>
                <input
                  type="password"
                  formControlName="password"
                  class="input-field"
                  placeholder="Enter new password"
                  maxlength="20"
                />
              </label>

              <label class="form-control">
                <span class="form-label">Confirm New Password</span>
                <input
                  type="password"
                  formControlName="password_confirmation"
                  class="input-field"
                  placeholder="Confirm new password"
                  maxlength="20"
                />
              </label>

              <div class="password-rules" aria-live="polite">
                <p
                  [class.rule-valid]="hasValidResetPasswordLength()"
                  [class.rule-invalid]="hasResetPasswordInput() && !hasValidResetPasswordLength()"
                >
                  <i class="bi" [ngClass]="hasValidResetPasswordLength() ? 'bi-check-circle-fill' : 'bi-circle'" aria-hidden="true"></i>
                  Between 8-20 characters
                </p>
                <p
                  [class.rule-valid]="hasResetPasswordUppercase()"
                  [class.rule-invalid]="hasResetPasswordInput() && !hasResetPasswordUppercase()"
                >
                  <i class="bi" [ngClass]="hasResetPasswordUppercase() ? 'bi-check-circle-fill' : 'bi-circle'" aria-hidden="true"></i>
                  At least 1 uppercase letter
                </p>
                <p
                  [class.rule-valid]="hasResetPasswordNumber()"
                  [class.rule-invalid]="hasResetPasswordInput() && !hasResetPasswordNumber()"
                >
                  <i class="bi" [ngClass]="hasResetPasswordNumber() ? 'bi-check-circle-fill' : 'bi-circle'" aria-hidden="true"></i>
                  At least 1 number
                </p>
                <p
                  [class.rule-valid]="hasResetPasswordSpecialCharacter()"
                  [class.rule-invalid]="hasResetPasswordInput() && !hasResetPasswordSpecialCharacter()"
                >
                  <i class="bi" [ngClass]="hasResetPasswordSpecialCharacter() ? 'bi-check-circle-fill' : 'bi-circle'" aria-hidden="true"></i>
                  At least 1 special character !&#64;#$%^&amp;*()&#45;+=_,.
                </p>
                <p
                  [class.rule-valid]="resetPasswordsMatch()"
                  [class.rule-invalid]="resetPasswordMismatch()"
                >
                  <i class="bi" [ngClass]="resetPasswordsMatch() ? 'bi-check-circle-fill' : 'bi-circle'" aria-hidden="true"></i>
                  Passwords must match
                </p>
              </div>

              @if (resetError()) {
                <div class="alert-error">
                  {{ resetError() }}
                </div>
              }

              <button
                type="submit"
                class="btn-primary w-full mt-2"
                [disabled]="resetSending() || !canSubmitPasswordReset()"
              >
                {{ resetSending() ? 'Saving...' : 'Set New Password' }}
              </button>

              <button type="button" class="btn-secondary w-full" (click)="backToLogin()">
                Back to Login
              </button>
            </form>
          } @else {
            <form
              [formGroup]="resetForm"
              (ngSubmit)="resetOtpSent() ? verifyPasswordReset() : requestPasswordReset()"
              class="auth-form"
            >
            <label class="form-control">
              <span class="form-label">Email Address</span>
              <input
                type="email"
                formControlName="email"
                class="input-field"
                placeholder="visitor@example.com"
                maxlength="60"
              />
            </label>

            @if (resetOtpSent()) {
              <label class="form-control">
                <span class="form-label">6-Digit OTP</span>
                <input
                  type="text"
                  formControlName="otp"
                  class="input-field"
                  placeholder="000000"
                  maxlength="6"
                  inputmode="numeric"
                />
              </label>

              <div class="resend-row">
                <span class="resend-help">Didn't receive the code?</span>
                <button
                  type="button"
                  class="resend-button"
                  (click)="resendPasswordResetOtp()"
                  [disabled]="resetSending() || resendCooldown() > 0"
                >
                  {{
                    resendCooldown() > 0
                      ? 'Resend in ' + resendCooldown() + 's'
                      : 'Resend OTP'
                  }}
                </button>
              </div>
            }

            @if (resetError()) {
              <div class="alert-error">
                {{ resetError() }}
              </div>
            }

            <button type="submit" class="btn-primary w-full mt-2" [disabled]="resetSending()">
              {{ resetSending() ? 'Processing...' : (resetOtpSent() ? 'Verify OTP' : 'Send OTP') }}
            </button>

            <button type="button" class="btn-secondary w-full" (click)="backToLogin()">
              Back to Login
            </button>
          </form>
          }
        }
      </div>
    </div>
  </section>
</dialog>
